(() => {
  'use strict';

  // ── ARIA tab pattern ────────────────────────────────────────
  function initTabs(tablist) {
    const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
    if (tabs.length === 0) return;

    function activateTab(activeTab) {
      tabs.forEach((tab) => {
        const panelId = tab.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;
        const isActive = tab === activeTab;

        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        tab.classList.toggle('product-tabs__button--active', isActive);

        if (panel) {
          if (isActive) {
            panel.removeAttribute('hidden');
          } else {
            panel.setAttribute('hidden', '');
          }
        }
      });

      activeTab.focus();
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activateTab(tab));

      tab.addEventListener('keydown', (e) => {
        let newIndex;
        if (e.key === 'ArrowRight') {
          newIndex = (index + 1) % tabs.length;
        } else if (e.key === 'ArrowLeft') {
          newIndex = (index - 1 + tabs.length) % tabs.length;
        } else if (e.key === 'Home') {
          newIndex = 0;
        } else if (e.key === 'End') {
          newIndex = tabs.length - 1;
        } else {
          return;
        }
        e.preventDefault();
        activateTab(tabs[newIndex]);
      });
    });
  }

  function initMiniPdp(sectionEl) {
    // ── Tabs ─────────────────────────────────────────────────
    sectionEl.querySelectorAll('[role="tablist"]').forEach(initTabs);

    // ── Gallery ──────────────────────────────────────────────
    const slides = Array.from(sectionEl.querySelectorAll('[data-slide]'));
    const thumbs = Array.from(sectionEl.querySelectorAll('[data-thumb]'));
    const dots = Array.from(sectionEl.querySelectorAll('[data-dot]'));
    const prevBtn = sectionEl.querySelector('[data-gallery-prev]');
    const nextBtn = sectionEl.querySelector('[data-gallery-next]');
    let currentIndex = 0;

    function goTo(index) {
      if (!slides.length) return;
      currentIndex = ((index % slides.length) + slides.length) % slides.length;
      slides.forEach((s, i) => s.classList.toggle('is-active', i === currentIndex));
      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === currentIndex));
      dots.forEach((d, i) => d.classList.toggle('is-active', i === currentIndex));
    }

    prevBtn?.addEventListener('click', () => goTo(currentIndex - 1));
    nextBtn?.addEventListener('click', () => goTo(currentIndex + 1));
    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => goTo(i)));

    // ── Purchase type toggle ─────────────────────────────────
    const purchaseOptions = Array.from(sectionEl.querySelectorAll('[data-purchase-type]'));
    const sellingPlanInput = sectionEl.querySelector('.lp-mini-pdp__selling-plan-input');
    const atcPriceEl = sectionEl.querySelector('.lp-mini-pdp__atc-price');
    const atcComparePriceEl = sectionEl.querySelector('.lp-mini-pdp__atc-compare-price');
    const otpOption = sectionEl.querySelector('[data-purchase-type="otp"]');

    function formatMoney(cents) {
      return (cents / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      });
    }

    function selectPurchaseType(optionEl) {
      purchaseOptions.forEach((opt) => {
        const selected = opt === optionEl;
        opt.classList.toggle('is-selected', selected);
        opt.setAttribute('aria-checked', selected ? 'true' : 'false');

        const radio = opt.querySelector('.lp-mini-pdp__radio');
        radio?.classList.toggle('lp-mini-pdp__radio--active', selected);
      });

      const purchaseType = optionEl.dataset.purchaseType;
      const sellingPlan = optionEl.dataset.sellingPlan;
      const price = parseInt(optionEl.dataset.price, 10);

      if (sellingPlanInput) {
        if (purchaseType === 'subscribe' && sellingPlan) {
          sellingPlanInput.value = sellingPlan;
          sellingPlanInput.disabled = false;
        } else {
          sellingPlanInput.value = '';
          sellingPlanInput.disabled = true;
        }
      }

      if (atcPriceEl && !isNaN(price)) {
        atcPriceEl.textContent = formatMoney(price);
      }

      // Show / hide compare price in ATC button
      if (atcComparePriceEl) {
        if (purchaseType === 'subscribe' && otpOption) {
          const otpPrice = parseInt(otpOption.dataset.price, 10);
          if (!isNaN(otpPrice)) {
            atcComparePriceEl.textContent = formatMoney(otpPrice);
          }
          atcComparePriceEl.style.display = '';
        } else {
          atcComparePriceEl.style.display = 'none';
        }
      }
    }

    purchaseOptions.forEach((opt) => {
      opt.addEventListener('click', () => selectPurchaseType(opt));
      opt.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectPurchaseType(opt);
        }
      });
    });
  }

  // ── Reviews anchor smooth scroll ────────────────────────────
  // The Stamped widget loads async, so its position is unknown on first click.
  // We intercept the click and retry until the target has a non-zero height.
  function getStickyHeaderOffset() {
    const header = document.querySelector('sticky-header, [data-sticky-header], .shopify-section-header-sticky, header[class*="sticky"]');
    return header ? header.offsetHeight : 0;
  }

  function scrollToReviews(e) {
    const href = e.currentTarget.getAttribute('href');
    if (!href || !href.startsWith('#')) return;
    const target = document.querySelector(href);
    if (!target) return;
    e.preventDefault();

    function doScroll() {
      const top = target.getBoundingClientRect().top + window.scrollY - getStickyHeaderOffset();
      window.scrollTo({ top, behavior: 'smooth' });
    }

    if (target.offsetHeight > 0) {
      doScroll();
    } else {
      // Widget not rendered yet — wait up to 3 s
      let attempts = 0;
      const interval = setInterval(() => {
        if (target.offsetHeight > 0 || ++attempts >= 30) {
          clearInterval(interval);
          doScroll();
        }
      }, 100);
    }
  }

  document.querySelectorAll('a[href="#stamped-main-widget"]').forEach((link) => {
    link.addEventListener('click', scrollToReviews);
  });

  // Init all sections present on page load
  document.querySelectorAll('.lp-mini-pdp').forEach(initMiniPdp);

  // Re-init when the customizer reloads the section
  document.addEventListener('shopify:section:load', (event) => {
    const sectionEl = event.target.querySelector('.lp-mini-pdp');
    if (sectionEl) initMiniPdp(sectionEl);
  });
})();
